import {
  formatMoneyRs as formatCurrency,
  parseMoney as parseCurrency,
  formatCurrencyInput,
} from "../../../utils/money";

export { formatCurrency, parseCurrency, formatCurrencyInput };
export const UNIDADES = ["kg", "g", "mg", "l", "ml", "un", "m"] as const;

export type UnidadeType = (typeof UNIDADES)[number];

export const ALERGENOS = [
  { id: "soja", label: "Soja", className: "soja" },
  { id: "leite", label: "Leite", className: "leite" },
  { id: "carne", label: "Carne de Gado", className: "carne" },
  { id: "frango", label: "Frango", className: "frango" },
] as const;

export type AlergenoId = (typeof ALERGENOS)[number]["id"];

export const DEFAULT_CARDAPIO_COMMISSION_PERCENT = 0;

export function effectiveCommissionPercent(
  value: number | "" | undefined | null,
): number {
  if (value === "" || value === undefined || value === null) {
    return DEFAULT_CARDAPIO_COMMISSION_PERCENT;
  }
  return Number(value);
}

/** Rótulo do item para selects e listas (fallback se o nome vier vazio da API). */
export function cardapioItemOptionLabel(item: { _id: number; name?: string }): string {
  const name = String(item.name ?? "").trim();
  if (name) return name;
  return item._id > 0 ? `Item #${item._id}` : "Item sem nome";
}

export function getAlergenoLabel(id: string): string {
  return ALERGENOS.find((a) => a.id === id)?.label || id;
}

export function resolveSectorLabel(
  item: { _id_sector?: number; sector_name?: string },
  setores: Array<{ _id: number; name: string }>,
): string {
  if (item.sector_name) return item.sector_name;
  if (!item._id_sector) return "-";
  return setores.find((s) => s._id === item._id_sector)?.name || "-";
}

export interface CardapioCartLine {
  _id_cardapio_item: number;
  item_name: string;
  quantity: number;
  unit_price: number;
}

export function normalizeWristbandKey(wristband: string): string {
  return wristband.trim().toLowerCase();
}

/** Pulseira real — ignora flag booleana ("0"/"1") vinda do campo mestre. */
export function isWristbandCode(value?: string | number | null): boolean {
  const key = normalizeWristbandKey(String(value ?? ""));
  if (!key) return false;
  return key !== "0" && key !== "1" && key !== "true" && key !== "false";
}

export function collectClienteWristbands(
  cliente?: {
    cliente_id?: number;
    cpf?: string | null;
    pulseiras?: Array<{ conteudo?: string | null; mestre?: string | null }>;
  } | null,
  extra: Array<string | number | null | undefined> = [],
): string[] {
  const keys = new Set<string>();
  for (const pulseira of cliente?.pulseiras || []) {
    if (isWristbandCode(pulseira.conteudo)) {
      keys.add(normalizeWristbandKey(String(pulseira.conteudo)));
    }
    if (isWristbandCode(pulseira.mestre)) {
      keys.add(normalizeWristbandKey(String(pulseira.mestre)));
    }
  }
  if (isWristbandCode(cliente?.cpf)) {
    keys.add(normalizeWristbandKey(String(cliente?.cpf)));
  }
  if (Number(cliente?.cliente_id || 0) > 1) {
    keys.add(String(cliente?.cliente_id));
  }
  for (const extraBand of extra) {
    if (isWristbandCode(extraBand)) {
      keys.add(normalizeWristbandKey(String(extraBand)));
    }
  }
  return Array.from(keys);
}

export function pedidoMatchesClient(
  pedido: { _id_usuario?: number | null; wristband_number?: string },
  userId: number,
  wristbands: string[],
): boolean {
  if (userId > 0 && Number(pedido._id_usuario || 0) === userId) return true;
  const band = normalizeWristbandKey(String(pedido.wristband_number || ""));
  if (band && wristbands.includes(band)) return true;
  if (userId > 1 && band === String(userId)) return true;
  return false;
}

export function pedidosByWristband<T extends { wristband_number: string }>(
  pedidos: T[],
  wristband: string,
): T[] {
  const key = normalizeWristbandKey(wristband);
  if (!key) return [];
  return pedidos.filter(
    (pedido) => normalizeWristbandKey(pedido.wristband_number) === key,
  );
}

export function latestPedidoByWristband<T extends { _id: number; wristband_number: string }>(
  pedidos: T[],
  wristband: string,
): T | undefined {
  const matches = pedidosByWristband(pedidos, wristband);
  if (!matches.length) return undefined;
  return matches.reduce((latest, pedido) =>
    pedido._id > latest._id ? pedido : latest,
  );
}

export function filterPedidosByWristbandSearch<T extends { wristband_number: string }>(
  pedidos: T[],
  search: string,
): T[] {
  const term = normalizeWristbandKey(search);
  if (!term) return pedidos;
  return pedidos.filter((pedido) =>
    normalizeWristbandKey(pedido.wristband_number).includes(term),
  );
}

export function mergePedidoCartLines(
  pedidos: Array<{
    items: Array<{
      _id_cardapio_item: number;
      item_name: string;
      quantity: number;
      unit_price: number;
    }>;
  }>,
): CardapioCartLine[] {
  const merged = new Map<number, CardapioCartLine>();

  for (const pedido of pedidos) {
    for (const item of pedido.items) {
      const existing = merged.get(item._id_cardapio_item);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        merged.set(item._id_cardapio_item, {
          _id_cardapio_item: item._id_cardapio_item,
          item_name: item.item_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
        });
      }
    }
  }

  return Array.from(merged.values());
}

export function sumPedidosTotal(
  pedidos: Array<{ total: number }>,
): number {
  return pedidos.reduce((sum, pedido) => sum + Number(pedido.total || 0), 0);
}

export function sumPedidosCommission(
  pedidos: Array<{
    total: number;
    commission_percent?: number;
    commission_value?: number;
  }>,
): number {
  return pedidos.reduce((sum, pedido) => {
    const explicit = Number(pedido.commission_value);
    if (!Number.isNaN(explicit) && explicit > 0) {
      return sum + explicit;
    }
    const percent = effectiveCommissionPercent(pedido.commission_percent);
    const total = Number(pedido.total || 0);
    return sum + Math.round((total * percent) / 100 * 100) / 100;
  }, 0);
}

export function formatPedidosWaiterNames(
  pedidos: Array<{ waiter_name: string }>,
): string {
  const names = Array.from(
    new Set(
      pedidos
        .map((pedido) => pedido.waiter_name.trim())
        .filter(Boolean),
    ),
  );
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  return names.join(", ");
}

function escapeHtml(value: string): string {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export interface ConferenceNoteLine {
  item_name: string;
  quantity: number;
  unit_price: number;
}

export interface ConferenceNotePrintData {
  /** Nome do cliente da compra (exibido como "Cliente:"). */
  clientName: string;
  issuedAt: string;
  lines: ConferenceNoteLine[];
  total: number;
  commissionPercent?: number;
  commissionValue?: number;
}

/** Opens a dedicated print window for the conference note (avoids Metronic blank print). */
export function printConferenceNote(data: ConferenceNotePrintData): boolean {
  const rows = data.lines
    .map((line) => {
      const lineTotal = line.unit_price * line.quantity;
      return `<tr class="item-name-row">
        <td colspan="3">${escapeHtml(line.item_name)}</td>
      </tr>
      <tr class="item-vals-row">
        <td class="num">${line.quantity}</td>
        <td class="num">${escapeHtml(formatCurrency(line.unit_price))}</td>
        <td class="num">${escapeHtml(formatCurrency(lineTotal))}</td>
      </tr>`;
    })
    .join("");

  const commissionHtml =
    Number(data.commissionValue) > 0
      ? `<div class="meta">Comissão (${Number(data.commissionPercent) || 0}%): ${escapeHtml(
          formatCurrency(Number(data.commissionValue)),
        )}</div>`
      : "";

  const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8" />
<title>Nota de Conferência</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; padding: 16px; font-family: "Courier New", Courier, monospace; color: #000; }
  h1 { font-size: 16px; text-align: center; margin: 0 0 12px; text-transform: uppercase; }
  .meta { font-size: 12px; margin: 2px 0; }
  .divider { border-top: 1px dashed #000; margin: 10px 0; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th, td { padding: 4px 0; vertical-align: top; }
  th { text-align: left; border-bottom: 1px solid #000; }
  td.num, th.num { text-align: right; white-space: nowrap; }
  tr.item-name-row td { font-weight: bold; padding-top: 8px; width: 100%; }
  tr.item-vals-row td { padding-bottom: 6px; border-bottom: 1px dotted #999; width: 33.33%; }
  .total { display: flex; justify-content: space-between; font-weight: bold; font-size: 13px; margin-top: 8px; }
  @page { margin: 8mm; size: 80mm auto; }
</style></head><body>
  <h1>Nota de Conferência</h1>
  <div class="meta">Cliente: ${escapeHtml(data.clientName || "—")}</div>
  <div class="meta">Emissão: ${escapeHtml(data.issuedAt || "—")}</div>
  <div class="divider"></div>
  <table>
    <thead><tr><th class="num">Qtd</th><th class="num">Unit.</th><th class="num">Total</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="divider"></div>
  <div class="total"><span>TOTAL</span><span>${escapeHtml(formatCurrency(data.total))}</span></div>
  ${commissionHtml}
</body></html>`;

  const win = window.open("", "_blank", "width=420,height=640");
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => {
    try {
      win.print();
    } catch {
      // ignore
    }
  }, 250);
  return true;
}
